import React, { useState, useEffect } from "react";
import { AdminMessages } from "../../hooks/APIHooks";

const AdminMessageCenter = () => {
  const [allMessages, setAllMessages] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [reply, setReply] = useState("");
  const [statusUpdatingIndex, setStatusUpdatingIndex] = useState(null);

  // Fetch all messages
  useEffect(() => {
    fetch(AdminMessages)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setAllMessages(data.messages);
        }
      })
      .catch((err) => console.error("Error fetching messages:", err));
  }, []);

  // Handle reply to message
  const handleReply = async (index) => {
    const msg = allMessages[index];
    const updatedMessage = msg.message + `admin: ${reply.trim()};`;

    try {
      const response = await fetch(AdminMessages, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: msg.email,
          subject: msg.subject,
          message: updatedMessage, // Include the updated message
        }),
      });

      const result = await response.json();
      if (result.success) {
        alert("Reply sent!");
        setReply("");
        setSelectedIndex(null);
        const updated = [...allMessages];
        updated[index].message = updatedMessage;
        setAllMessages(updated);
      } else {
        alert("Failed to send reply.");
      }
    } catch (error) {
      alert("Error replying. Try again.");
    }
  };

  // Handle status change
  const handleStatusChange = async (index, newStatus) => {
    setStatusUpdatingIndex(index);
    const msg = allMessages[index];

    try {
      const response = await fetch(AdminMessages, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: msg.id, // Send the message ID
          status: newStatus,
        }),
      });

      const result = await response.json();
      if (result.success) {
        const updated = [...allMessages];
        updated[index].status = newStatus;
        setAllMessages(updated);
      } else {
        alert("Failed to update status.");
      }
    } catch (error) {
      alert("Error updating status. Try again.");
    } finally {
      setStatusUpdatingIndex(null);
    }
  };

  // Handle delete message
  const handleDelete = async (index) => {
    const msg = allMessages[index];
    if (
      !window.confirm(
        `Are you sure you want to delete message with subject "${msg.subject}"?`
      )
    ) {
      return;
    }

    try {
      const response = await fetch(AdminMessages, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: msg.id }), // Send the message ID
      });

      const result = await response.json();
      if (result.success) {
        alert("Message deleted successfully.");
        const updated = [...allMessages];
        updated.splice(index, 1);
        setAllMessages(updated);
        if (selectedIndex === index) setSelectedIndex(null);
      } else {
        alert("Failed to delete message.");
      }
    } catch (error) {
      alert("Error deleting message. Try again.");
    }
  };

  return (
    <section className="p-6 bg-gray-100 min-h-screen pt-24">
      <h1 className="text-xl font-bold mb-4">Admin Message Center</h1>

      {allMessages.length > 0 ? (
        allMessages.map((msg, index) => (
          <div key={index} className="bg-white p-4 rounded shadow mb-4">
            <p className="text-sm font-semibold">Subject: {msg.subject}</p>

            <p className="text-xs text-gray-500 mb-2">Name: {msg.name}</p>
            <p className="text-xs text-gray-500 mb-2">
              Details: {msg.email} || {msg.number}{" "}
            </p>

            <div className="flex items-center gap-3 mb-2">
              <span>Status:</span>
              <select
                disabled={statusUpdatingIndex === index}
                value={msg.status}
                onChange={(e) => handleStatusChange(index, e.target.value)}
                className="border rounded p-1"
              >
                <option value="Pending">Pending</option>
                <option value="Review">Review</option>
                <option value="Closed">Closed</option>
              </select>
              {statusUpdatingIndex === index && <span>Updating...</span>}
            </div>

            <div className="text-sm space-y-1 text-black bg-gray-50 p-2 rounded">
              {msg.message
                .split(";")
                .map((line, idx) =>
                  line.trim() ? <p key={idx}>• {line.trim()}</p> : null
                )}
            </div>

            {selectedIndex === index ? (
              <div className="mt-3 space-y-2">
                <textarea
                  className="w-full p-2 border rounded"
                  placeholder="Write your reply..."
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => handleReply(index)}
                    className="bg-green-500 text-white px-4 py-1 rounded"
                  >
                    Send Reply
                  </button>
                  <button
                    onClick={() => setSelectedIndex(null)}
                    className="bg-gray-300 px-4 py-1 rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => setSelectedIndex(index)}
                  className="bg-blue-500 text-white px-4 py-1 rounded"
                >
                  Reply
                </button>
                <button
                  onClick={() => handleDelete(index)}
                  className="bg-red-600 text-white px-4 py-1 rounded"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        ))
      ) : (
        <p>No messages found.</p>
      )}
    </section>
  );
};

export default AdminMessageCenter;
